import { Skeleton } from '@mui/material';
import { Flex } from '../Flex';
import { ReactComponent as AcknowledgedIcon } from '../../assets/20x20/incident-acknowledged.svg';
import { ReactComponent as OpenIcon } from '../../assets/20x20/incident-open.svg';
import useSharedVisibility from '../../hooks/useSharedVisibility';
import type { FilterItem } from '../../hooks/useUrlFilters';
import type { IncidentV2StatusCounts } from '../../services/gql/types';
import type { TreeTotalsType, TreeType, TreeViewBy } from '../../types';
import Chip from '../Chip/Chip';
import SvgIcon from '../SvgIcon';
import { useTreeNodeIncidentStatusTotals } from './hooks/useTreeNodeIncidentStatusTotals';
import AsyncTreeNodeChips from './AsyncTreeNodeChips';

export type AsyncTreeNodeIncidentStatusChipsProps = {
  category: TreeType;
  view: TreeViewBy;
  daysBack: number;
  filters: FilterItem[];
  kind: TreeTotalsType;
  requestKey: string;
};

const StatusSkeleton = () => (
  <Skeleton variant="rounded" width={16} height={20} sx={{ flexShrink: 0 }} />
);

const IncidentStatusChips = ({
  statusCounts,
}: {
  statusCounts: IncidentV2StatusCounts;
}) => {
  const { open, acknowledged } = statusCounts;
  if (open === 0 && acknowledged === 0) {
    return null;
  }
  return (
    <Flex sx={{ gap: 0.6, alignItems: 'center' }}>
      {open > 0 && (
        <Chip
          color="error"
          size="small"
          icon={
            <SvgIcon width={12}>
              <OpenIcon />
            </SvgIcon>
          }
          label={open}
        />
      )}
      {acknowledged > 0 && (
        <Chip
          color="warning"
          size="small"
          icon={
            <SvgIcon width={12}>
              <AcknowledgedIcon />
            </SvgIcon>
          }
          label={acknowledged}
        />
      )}
    </Flex>
  );
};

const AsyncTreeNodeIncidentStatusChips = (
  props: AsyncTreeNodeIncidentStatusChipsProps,
) => {
  const [ref, visible] = useSharedVisibility<HTMLDivElement>('100px');
  const { data, isLoading } = useTreeNodeIncidentStatusTotals({
    ...props,
    enabled: visible,
  });

  return (
    <AsyncTreeNodeChips<IncidentV2StatusCounts>
      containerRef={ref}
      visible={visible}
      data={data}
      isLoading={isLoading}
      renderSkeleton={() => <StatusSkeleton />}
      renderContent={(statusCounts) => (
        <IncidentStatusChips statusCounts={statusCounts} />
      )}
    />
  );
};

export default AsyncTreeNodeIncidentStatusChips;
